import React, { useState } from 'react';
import { Search, MapPin, Play } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VenueSearch = () => {
  const [venueInput, setVenueInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const searchVideos = async (venue) => {
    const query = venue || venueInput;
    if (!query.trim()) return;
    setSearching(true);
    setSearchQuery(query);
    try {
      const res = await fetch(`${BACKEND_URL}/api/search-venue-videos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ venue_name: query }),
      });
      const data = await res.json();
      setVideos(data.videos || []);
      setSearchPerformed(true);
      setSelectedVideo(null);
    } catch {
      setVideos([]);
      setSearchPerformed(true);
    } finally {
      setSearching(false);
    }
  };

  return (
    <section data-testid="venue-search-section" className="py-24" style={{ backgroundColor: '#FAF8F5' }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <div className="text-center mb-12">
          <MapPin className="w-10 h-10 text-gold mx-auto mb-4" />
          <h2 className="font-serif text-4xl sm:text-5xl text-gray-900 mb-4">
            We've captured hundreds of weddings at almost every venue in Essex
          </h2>
          <p className="text-gray-600 text-lg">
            Search your venue to see our work.
          </p>
        </div>

        {/* Search */}
        <div className="max-w-2xl mx-auto mb-6">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                data-testid="venue-search-input"
                type="text"
                placeholder="Enter your venue name..."
                value={venueInput}
                onChange={(e) => setVenueInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchVideos()}
                className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-all bg-white"
              />
            </div>
            <button
              data-testid="venue-search-button"
              onClick={() => searchVideos()}
              disabled={searching}
              className="bg-black hover:bg-black/80 text-white font-semibold px-8 py-4 rounded-xl transition-all disabled:opacity-50"
            >
              {searching ? 'Searching...' : 'Search'}
            </button>
          </div>

          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <span className="text-gray-500 text-sm">Try:</span>
            {['Gaynes Park', 'Baddow Park', 'High House', 'Leez Priory', 'Gosfield Hall', 'Braxted Park'].map((v) => (
              <button
                key={v}
                data-testid={`popular-venue-${v.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => { setVenueInput(v); searchVideos(v); }}
                disabled={searching}
                className="text-sm px-3 py-1.5 bg-white border border-gray-200 hover:border-gold hover:text-gold rounded-full transition-all disabled:opacity-50"
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        {/* Results */}
        {searchPerformed && (
          <div data-testid="venue-search-results" className="mt-10">
            {videos.length > 0 ? (
              <>
                <p className="text-center text-gray-900 font-semibold text-lg mb-6">
                  Our films from {searchQuery}
                </p>

                {selectedVideo && (
                  <div className="mb-8 animate-fade-up">
                    <div className="aspect-video rounded-xl overflow-hidden shadow-2xl">
                      <iframe
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/${selectedVideo.id}?autoplay=1&rel=0&modestbranding=1`}
                        title={selectedVideo.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {videos.map((video) => (
                    <div
                      key={video.id}
                      data-testid={`venue-video-${video.id}`}
                      className="relative aspect-video rounded-xl overflow-hidden cursor-pointer group shadow-md"
                      onClick={() => setSelectedVideo(video)}
                    >
                      <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-all flex items-center justify-center">
                        <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Play className="w-5 h-5 text-black ml-0.5" />
                        </div>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70">
                        <p className="text-white text-xs font-medium line-clamp-1">{video.title}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Personalised venue CTA */}
                <div data-testid="venue-personalised-cta" className="mt-10 rounded-2xl p-8 sm:p-10 text-center" style={{ backgroundColor: '#1A1A1A' }}>
                  <p className="text-gold text-xs font-bold uppercase tracking-widest mb-3">Your venue. Your film.</p>
                  <h3 className="font-serif text-2xl sm:text-3xl text-white mb-3">
                    We've captured weddings at {searchQuery}. Couples there love working with us.
                  </h3>
                  <p className="text-white/60 mb-6">Want to be next?</p>
                  <button
                    onClick={() => window.location.href = 'https://www.weddingvideo.co.uk/contact'}
                    className="bg-gold hover:bg-gold-light text-black font-bold px-8 py-4 rounded-xl transition-all hover:scale-105 text-sm"
                  >
                    Secure My Date
                  </button>
                </div>
              </>
            ) : (
              <div className="rounded-2xl p-8 sm:p-10 text-center" style={{ backgroundColor: '#1A1A1A' }}>
                <p className="text-gold text-xs font-bold uppercase tracking-widest mb-3">Your venue. Your film.</p>
                <h3 className="font-serif text-2xl sm:text-3xl text-white mb-3">
                  We'd love {searchQuery} to be our next venue.
                </h3>
                <p className="text-white/60 mb-6">We've filmed at hundreds of venues across Essex and beyond. Let's make it happen.</p>
                <button
                  onClick={() => window.location.href = 'https://www.weddingvideo.co.uk/contact'}
                  className="bg-gold hover:bg-gold-light text-black font-bold px-8 py-4 rounded-xl transition-all hover:scale-105 text-sm"
                >
                  Secure My Date
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default VenueSearch;
